import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import { Box, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { WysslingResult } from "../../types/interfaces/methods/WysslingResult";

interface WysslingResultsProps {
  calculations: WysslingResult[];
}

export default function WysslingResults({ calculations }: Readonly<WysslingResultsProps>) {
  const { t } = useTranslation();

  const formatDecimals = (value?: number | null) =>
    typeof value === "number"
      ? value.toLocaleString(undefined, { maximumFractionDigits: 3 })
      : "-";

  //GRID 1
  const grid1Columns = (t: any): GridColDef[] => [
    {
      field: "gradient",
      headerName: t("calculations.methods.wyssling.results.gradienteHidraulico"),
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
    },
    {
      field: "zonaTotal",
      headerName: t("calculations.methods.wyssling.results.zonaChamadaTotal"),
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
    },
    {
      field: "zonaLateral",
      headerName: t("calculations.methods.wyssling.results.zonaChamadaLateral"),
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
    },
    {
      field: "raio",
      headerName: `${t("calculations.methods.wyssling.results.raioChamada")}`,
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
    },
    {
      field: "velocidade",
      headerName: `${t("calculations.methods.wyssling.results.velocidadeEficaz")}`,
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
    },
  ];

  const grid1Rows = (r: any) => [
    {
      id: "base",
      type: "base",
      gradient: r.baseGradient.i,
      zonaTotal: r.baseGradient.wB,
      zonaLateral: r.baseGradient.wBl,
      raio: r.baseGradient.wX0,
      velocidade: r.baseGradient.wVe,
    },
    ...r.userGradients.map((ug: any, i: number) => ({
      id: i,
      type: "user",
      gradient: ug.iW,
      zonaTotal: ug.iwB,
      zonaLateral: ug.iwBl,
      raio: ug.iwX0,
      velocidade: ug.iwVe,
    })),
    {
      id: "avg",
      type: "avg",
      gradient: r.averages.gradienteHidraulico,
      zonaTotal: r.averages.zonaChamadaTotal,
      zonaLateral: r.averages.zonaChamadaLateral,
      raio: r.averages.raioChamada,
      velocidade: r.averages.velocidadeEficaz,
    },
  ];

  //GRID 2A (1 dia)
  const gridDistance1Columns = (t: any): GridColDef[] => [
    {
      field: "gradient",
      headerName: t("calculations.methods.wyssling.results.gradienteHidraulico"),
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
    },
    {
      field: "Ve",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.wyssling.results.distancia1Dia")}
          <span style={{ fontWeight: 550 }}>Ve</span>
        </div>
      ),
    },
    {
      field: "S0",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.wyssling.results.distancia1Dia")}
          <span style={{ fontWeight: 550 }}>S0</span>
        </div>
      ),
    },
    {
      field: "SU",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.wyssling.results.distancia1Dia")}
          <span style={{ fontWeight: 550 }}>SU</span>
        </div>
      ),
    },
  ];

  const gridDistance1Rows = (r: any) => [
    {
      id: "base",
      gradient: r.baseGradient.i,
      Ve: r.baseGradient.wVe1dia,
      S0: r.baseGradient.wS01dia,
      SU: r.baseGradient.wSU1dia,
    },
    ...r.userGradients.map((ug: any, i: number) => ({
      id: i,
      gradient: ug.iW,
      Ve: ug.iwVe1dia,
      S0: ug.iwS01dia,
      SU: ug.iwSU1dia,
    })),
    {
      id: "avg",
      gradient: r.averages.gradienteHidraulico,
      Ve: r.averages.distancia1DiaVe,
      S0: r.averages.distancia1DiaS0,
      SU: r.averages.distancia1DiaSu,
    },
  ];

  //GRID 2B (50 dias)
  const gridDistance50Columns = (t: any): GridColDef[] => [
    {
      field: "gradient",
      headerName: t("calculations.methods.wyssling.results.gradienteHidraulico"),
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
    },
    {
      field: "Ve",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.wyssling.results.distancia50Dias")}
          <span style={{ fontWeight: 550 }}>Ve</span>
        </div>
      ),
    },
    {
      field: "S0",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.wyssling.results.distancia50Dias")}
          <span style={{ fontWeight: 550 }}>S0</span>
        </div>
      ),
    },
    {
      field: "SU",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.wyssling.results.distancia50Dias")}
          <span style={{ fontWeight: 550 }}>SU</span>
        </div>
      ),
    },
  ];

  const gridDistance50Rows = (r: any) => [
    {
      id: "base",
      gradient: r.baseGradient.i,
      Ve: r.baseGradient.wVe50dias,
      S0: r.baseGradient.wS050dias,
      SU: r.baseGradient.wSU50dias,
    },
    ...r.userGradients.map((ug: any, i: number) => ({
      id: i,
      gradient: ug.iW,
      Ve: ug.iwVe50dias,
      S0: ug.iwS050dias,
      SU: ug.iwSU50dias,
    })),
    {
      id: "avg",
      gradient: r.averages.gradienteHidraulico,
      Ve: r.averages.distancia50DiasVe,
      S0: r.averages.distancia50DiasS0,
      SU: r.averages.distancia50DiasSu,
    },
  ];

  //GRID 2C (3500 dias)
  const gridDistance3500Columns = (t: any): GridColDef[] => [
    {
      field: "gradient",
      headerName: t("calculations.methods.wyssling.results.gradienteHidraulico"),
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
    },
    {
      field: "Ve",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.wyssling.results.distancia3500Dias")}
          <span style={{ fontWeight: 550 }}>Ve</span>
        </div>
      ),
    },
    {
      field: "S0",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.wyssling.results.distancia3500Dias")}
          <span style={{ fontWeight: 550 }}>S0</span>
        </div>
      ),
    },
    {
      field: "SU",
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.wyssling.results.distancia3500Dias")}
          <span style={{ fontWeight: 550 }}>SU</span>
        </div>
      ),
    },
  ];

  const gridDistance3500Rows = (r: any) => [
    {
      id: "base",
      gradient: r.baseGradient.i,
      Ve: r.baseGradient.wVe3500dias,
      S0: r.baseGradient.wS03500dias,
      SU: r.baseGradient.wSU3500dias,
    },
    ...r.userGradients.map((ug: any, i: number) => ({
      id: i,
      gradient: ug.iW,
      Ve: ug.iwVe3500dias,
      S0: ug.iwS03500dias,
      SU: ug.iwSU3500dias,
    })),
    {
      id: "avg",
      gradient: r.averages.gradienteHidraulico,
      Ve: r.averages.distancia3500DiasVe,
      S0: r.averages.distancia3500DiasS0,
      SU: r.averages.distancia3500DiasSu,
    },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
        {t("calculations.methods.results-title")}
      </Typography>

      {calculations.map((calc, index) => {
        const r = calc.results;

        return (
          <Box key={index} sx={{ mb: 6 }}>
            {/* GRID 1 */}
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ fontWeight: 700, mt: 3, mb: 1, display: "flex", alignItems: "center", gap: 1, }}
            >
              <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />
              {t("calculations.methods.wyssling.results.grid1")}
            </Typography>

            <DataGrid
              rows={grid1Rows(r)}
              columns={grid1Columns(t).map(col => ({ ...col, sortable: false }))}
              hideFooter
              disableColumnMenu
              sx={{
                boxShadow: 1,
                "& .MuiDataGrid-row:last-child": {
                  fontWeight: 700,
                  borderTop: "3px solid",
                },
              }}
            />

            {/* GRID 2A — 1 DIA */}
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ fontWeight: 700, mt: 3, mb: 1, display: "flex", alignItems: "center", gap: 1, }}
            >
              <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />
              {t("calculations.methods.wyssling.results.distancia1Dia")}
            </Typography>
            <DataGrid
              rows={gridDistance1Rows(r)}
              columns={gridDistance1Columns(t).map(c => ({ ...c, sortable: false }))}
              hideFooter
              disableColumnMenu
              sx={{
                boxShadow: 1,
                "& .MuiDataGrid-row:last-child": {
                  fontWeight: 700,
                  borderTop: "3px solid",
                },
              }}
            />

            {/* GRID 2B — 50 DIAS */}
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ fontWeight: 700, mt: 3, mb: 1, display: "flex", alignItems: "center", gap: 1, }}
            >
              <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />
              {t("calculations.methods.wyssling.results.distancia50Dias")}
            </Typography>
            <DataGrid
              rows={gridDistance50Rows(r)}
              columns={gridDistance50Columns(t).map(c => ({ ...c, sortable: false }))}
              hideFooter
              disableColumnMenu
              sx={{
                boxShadow: 1,
                "& .MuiDataGrid-row:last-child": {
                  fontWeight: 700,
                  borderTop: "3px solid",
                },
              }}
            />

            {/* GRID 2C — 3500 DIAS */}
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ fontWeight: 700, mt: 3, mb: 1, display: "flex", alignItems: "left", gap: 1, }}
            >
              <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />
              {t("calculations.methods.wyssling.results.distancia3500Dias")}
            </Typography>
            <DataGrid
              rows={gridDistance3500Rows(r)}
              columns={gridDistance3500Columns(t).map(c => ({ ...c, sortable: false }))}
              hideFooter
              disableColumnMenu
              sx={{
                boxShadow: 1,
                "& .MuiDataGrid-row:last-child": {
                  fontWeight: 700,
                  borderTop: "3px solid",
                },
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
}